import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Tv,
  Globe,
  Share2,
  ShieldAlert,
  Search,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  Filter,
} from 'lucide-react';
import { adminApi } from '../services/api.js';
import type {
  AdminAdAttemptsResponse,
  AdminTaskAttemptsResponse,
  AdminReferralTransactionsResponse,
  AdminAuditLogsResponse,
  AdminAdAttemptItem,
  AdminTaskAttemptItem,
  AdminReferralTransactionItem,
  AdminAuditLogItem,
} from '@nova-mine/shared';
import { formatCoinAmount } from '@nova-mine/shared';

type ActivityTab = 'ads' | 'tasks' | 'referrals' | 'audit';

export const ActivityLogsView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActivityTab>('ads');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [adsData, setAdsData] = useState<AdminAdAttemptsResponse | null>(null);
  const [tasksData, setTasksData] = useState<AdminTaskAttemptsResponse | null>(null);
  const [referralsData, setReferralsData] = useState<AdminReferralTransactionsResponse | null>(null);
  const [auditData, setAuditData] = useState<AdminAuditLogsResponse | null>(null);

  // Filters
  const [adStatusFilter, setAdStatusFilter] = useState<'all' | 'started' | 'completed' | 'failed'>('all');

  const loadCurrentTabData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (activeTab === 'ads') {
        const res = await adminApi.getAdAttempts(100, adStatusFilter);
        setAdsData(res);
      } else if (activeTab === 'tasks') {
        const res = await adminApi.getTaskAttempts(100);
        setTasksData(res);
      } else if (activeTab === 'referrals') {
        const res = await adminApi.getReferralTransactions(100);
        setReferralsData(res);
      } else if (activeTab === 'audit') {
        const res = await adminApi.getAuditLogs(100);
        setAuditData(res);
      }
    } catch (err: unknown) {
      console.error('[ActivityLogs Load Error]', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch activity logs');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, adStatusFilter]);

  useEffect(() => {
    loadCurrentTabData();
  }, [loadCurrentTabData]);

  const formatDate = (ms: number | null) => {
    if (!ms) return 'N/A';
    return new Date(ms).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Sub-Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-white flex items-center space-x-2.5">
            <Activity className="w-5 h-5 text-purple-400" />
            <span>Activity, Task & Audit Feeds</span>
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Live telemetry of AdsGram view sequences, website visits, referral awards, and staff audit events.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => loadCurrentTabData()}
            disabled={isLoading}
            className="p-2.5 rounded-xl bg-space-850 hover:bg-space-800 text-gray-300 hover:text-white border border-white/5 transition-all"
            title="Refresh logs"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs bar */}
      <div className="flex border-b border-white/10 space-x-1 overflow-x-auto scrollbar-none">
        <button
          onClick={() => setActiveTab('ads')}
          className={`flex items-center space-x-2 px-4 py-3 text-xs font-bold border-b-2 transition-all whitespace-nowrap ${
            activeTab === 'ads'
              ? 'border-purple-500 text-purple-400 bg-purple-500/5'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <Tv className="w-4 h-4" />
          <span>AdsGram Views</span>
          {adsData && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-space-800 text-gray-300">
              {adsData.total}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('tasks')}
          className={`flex items-center space-x-2 px-4 py-3 text-xs font-bold border-b-2 transition-all whitespace-nowrap ${
            activeTab === 'tasks'
              ? 'border-purple-500 text-purple-400 bg-purple-500/5'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <Globe className="w-4 h-4" />
          <span>Website Tasks</span>
          {tasksData && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-space-800 text-gray-300">
              {tasksData.total}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('referrals')}
          className={`flex items-center space-x-2 px-4 py-3 text-xs font-bold border-b-2 transition-all whitespace-nowrap ${
            activeTab === 'referrals'
              ? 'border-purple-500 text-purple-400 bg-purple-500/5'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <Share2 className="w-4 h-4" />
          <span>Referral Bonuses</span>
          {referralsData && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-space-800 text-gray-300">
              {referralsData.total}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex items-center space-x-2 px-4 py-3 text-xs font-bold border-b-2 transition-all whitespace-nowrap ${
            activeTab === 'audit'
              ? 'border-purple-500 text-purple-400 bg-purple-500/5'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          <ShieldAlert className="w-4 h-4" />
          <span>Admin Audit Logs</span>
          {auditData && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-space-800 text-gray-300">
              {auditData.total}
            </span>
          )}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => loadCurrentTabData()}
            className="px-3 py-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. ADSGRAM ATTEMPTS TAB */}
      {/* ========================================================================= */}
      {activeTab === 'ads' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-space-900/60 p-3 rounded-2xl border border-white/5">
            <div className="flex items-center space-x-2">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs font-bold text-gray-300">Status Filter:</span>
              {(['all', 'completed', 'started', 'failed'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setAdStatusFilter(st)}
                  className={`px-3 py-1 rounded-xl text-xs font-bold capitalize transition-all ${
                    adStatusFilter === st
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-900/50'
                      : 'bg-space-800 text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>

            <div className="text-xs text-gray-400">
              Showing <span className="text-white font-semibold">{adsData?.attempts.length || 0}</span> events
            </div>
          </div>

          <div className="bg-space-900 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-space-950/80 text-gray-400 uppercase font-mono tracking-wider text-[11px]">
                    <th className="py-3 px-4">Attempt ID</th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Placement / Block ID</th>
                    <th className="py-3 px-4">Step</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Reward</th>
                    <th className="py-3 px-4 text-right">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {isLoading && !adsData ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-400" />
                        Loading ad attempts...
                      </td>
                    </tr>
                  ) : adsData?.attempts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500 font-sans">
                        No ad attempts recorded matching the filter.
                      </td>
                    </tr>
                  ) : (
                    adsData?.attempts.map((ad: AdminAdAttemptItem) => (
                      <tr key={ad.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4 text-gray-400 font-bold truncate max-w-[120px]">
                          {ad.id}
                        </td>
                        <td className="py-3 px-4 font-sans font-medium text-white">
                          <div>{ad.user_name || 'Anonymous'}</div>
                          <div className="text-[10px] font-mono text-cyan-400">{ad.telegram_id}</div>
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <span className="font-semibold text-purple-300">
                            {ad.placement_type === 'reward_interstitial' ? 'Interstitial Step 1' : 'Video Step 2'}
                          </span>
                          <div className="text-[10px] font-mono text-gray-500">{ad.block_id}</div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-0.5 rounded-md bg-space-800 text-gray-300 font-bold text-[10px]">
                            #{ad.sequence_step}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <span
                            className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                              ad.status === 'completed'
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : ad.status === 'started'
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                            }`}
                          >
                            {ad.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                            {ad.status === 'started' && <Clock className="w-3 h-3" />}
                            {ad.status === 'failed' && <XCircle className="w-3 h-3" />}
                            <span className="capitalize">{ad.status}</span>
                          </span>
                        </td>
                        <td className="py-3 px-4 text-nova-gold font-bold">
                          {ad.reward_coins > 0 ? `+${ad.reward_coins.toLocaleString()} NOVA` : '—'}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-400 font-sans text-[11px]">
                          {formatDate(ad.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. WEBSITE TASK ATTEMPTS TAB */}
      {/* ========================================================================= */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="bg-space-900 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-space-950/80 text-gray-400 uppercase font-mono tracking-wider text-[11px]">
                    <th className="py-3 px-4">Task ID</th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Task Title</th>
                    <th className="py-3 px-4">Min Dwell</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Completed At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {isLoading && !tasksData ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-400" />
                        Loading website task events...
                      </td>
                    </tr>
                  ) : tasksData?.tasks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-500 font-sans">
                        No website task completions recorded yet.
                      </td>
                    </tr>
                  ) : (
                    tasksData?.tasks.map((task: AdminTaskAttemptItem) => (
                      <tr key={task.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4 text-gray-400 font-bold truncate max-w-[120px]">
                          {task.id}
                        </td>
                        <td className="py-3 px-4 font-sans font-medium text-white">
                          <div>{task.user_name || 'Anonymous'}</div>
                          <div className="text-[10px] font-mono text-cyan-400">{task.telegram_id}</div>
                        </td>
                        <td className="py-3 px-4 font-sans font-semibold text-gray-200">
                          {task.task_title || task.task_id}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {task.min_dwell_seconds}s
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                            task.status === 'completed'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          }`}>
                            <CheckCircle2 className="w-3 h-3" />
                            <span className="capitalize">{task.status}</span>
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-400 font-sans text-[11px]">
                          {formatDate(task.completed_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. REFERRAL TRANSACTIONS TAB */}
      {/* ========================================================================= */}
      {activeTab === 'referrals' && (
        <div className="space-y-4">
          <div className="bg-space-900 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-space-950/80 text-gray-400 uppercase font-mono tracking-wider text-[11px]">
                    <th className="py-3 px-4">Tx ID</th>
                    <th className="py-3 px-4">Referrer (Awarded)</th>
                    <th className="py-3 px-4">Referee (New User)</th>
                    <th className="py-3 px-4">Bonus Issued</th>
                    <th className="py-3 px-4 text-right">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {isLoading && !referralsData ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-400" />
                        Loading referral transactions...
                      </td>
                    </tr>
                  ) : referralsData?.referrals.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-500 font-sans">
                        No referral bonuses recorded yet.
                      </td>
                    </tr>
                  ) : (
                    referralsData?.referrals.map((tx: AdminReferralTransactionItem) => (
                      <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4 text-gray-400 font-bold truncate max-w-[120px]">
                          {tx.id}
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <div className="text-white font-semibold">{tx.referrer_name || 'Unknown'}</div>
                          <div className="text-[10px] font-mono text-cyan-400">ID: {tx.referrer_id}</div>
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <div className="text-gray-300 font-medium">{tx.referee_name || 'Invited User'}</div>
                          <div className="text-[10px] font-mono text-gray-500">ID: {tx.referee_id}</div>
                        </td>
                        <td className="py-3 px-4 text-nova-gold font-bold text-sm">
                          +{formatCoinAmount(tx.reward_coins, 0)} NOVA
                        </td>
                        <td className="py-3 px-4 text-right text-gray-400 font-sans text-[11px]">
                          {formatDate(tx.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. ADMIN AUDIT LOGS TAB */}
      {/* ========================================================================= */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <div className="bg-space-900 border border-white/10 rounded-2xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-white/10 bg-space-950/80 text-gray-400 uppercase font-mono tracking-wider text-[11px]">
                    <th className="py-3 px-4">Log ID</th>
                    <th className="py-3 px-4">Administrator</th>
                    <th className="py-3 px-4">Action</th>
                    <th className="py-3 px-4">Target</th>
                    <th className="py-3 px-4">Audited Details</th>
                    <th className="py-3 px-4">IP Address</th>
                    <th className="py-3 px-4 text-right">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {isLoading && !auditData ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-400" />
                        Loading security audit trail...
                      </td>
                    </tr>
                  ) : auditData?.logs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-gray-500 font-sans">
                        No administrator actions recorded yet.
                      </td>
                    </tr>
                  ) : (
                    auditData?.logs.map((log: AdminAuditLogItem) => (
                      <tr key={log.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-3 px-4 text-gray-400 truncate max-w-[100px]">
                          {log.id}
                        </td>
                        <td className="py-3 px-4 font-sans font-bold text-purple-400">
                          {log.admin_username ? `@${log.admin_username}` : 'System / Root'}
                        </td>
                        <td className="py-3 px-4 font-sans">
                          <span className="px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-300 font-bold border border-purple-500/20 text-[10px]">
                            {log.action}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px] text-gray-300">
                          <span className="text-gray-500">{log.target_type}:</span> {log.target_id || 'global'}
                        </td>
                        <td className="py-3 px-4 font-mono text-[10px] text-gray-400 max-w-xs truncate" title={log.details || ''}>
                          {log.details || '—'}
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-[11px]">
                          {log.ip_address || '127.0.0.1'}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-400 font-sans text-[11px]">
                          {formatDate(log.created_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
